import React, { useEffect, useRef, useState } from 'react';
import Icon from 'components/AppIcon';

export default function GuidedTour({ active, currentStep, stepIndex, totalSteps, onNext, onPrev, onSkip, onComplete, isLastStep, isFirstStep, navigatingTo }) {
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [highlightStyle, setHighlightStyle] = useState({});
  const [isCentered, setIsCentered] = useState(false);

  useEffect(() => {
    if (!active || !currentStep) return;

    // Centered modal for steps with no target
    if (!currentStep?.target) {
      setIsCentered(true);
      setHighlightStyle({});
      setTooltipStyle({});
      return;
    }

    setIsCentered(false);

    const updatePosition = () => {
      const target = document.querySelector(currentStep?.target);
      if (!target) {
        // Target not found — fall back to centered
        setIsCentered(true);
        return;
      }

      setIsCentered(false);
      const rect = target?.getBoundingClientRect();
      const padding = 8;

      setHighlightStyle({
        position: 'fixed',
        top: rect?.top - padding,
        left: rect?.left - padding,
        width: rect?.width + padding * 2,
        height: rect?.height + padding * 2,
        borderRadius: '12px',
        boxShadow: '0 0 0 9999px rgba(0,0,0,0.65)',
        border: '2px solid rgba(255,255,255,0.8)',
        zIndex: 9998,
        pointerEvents: 'none',
        transition: 'all 0.3s ease',
      });

      const tooltipWidth = 320;
      const tooltipHeight = 220;
      const pos = currentStep?.position || 'bottom';
      let top, left;

      if (pos === 'bottom') {
        top = rect?.bottom + 14;
        left = rect?.left + rect?.width / 2 - tooltipWidth / 2;
      } else if (pos === 'top') {
        top = rect?.top - tooltipHeight - 14;
        left = rect?.left + rect?.width / 2 - tooltipWidth / 2;
      } else if (pos === 'right') {
        top = rect?.top + rect?.height / 2 - tooltipHeight / 2;
        left = rect?.right + 14;
      } else if (pos === 'left') {
        top = rect?.top + rect?.height / 2 - tooltipHeight / 2;
        left = rect?.left - tooltipWidth - 14;
      } else {
        top = rect?.bottom + 14;
        left = rect?.left + rect?.width / 2 - tooltipWidth / 2;
      }

      // Clamp to viewport
      left = Math.max(12, Math.min(left, window.innerWidth - tooltipWidth - 12));
      top = Math.max(12, Math.min(top, window.innerHeight - tooltipHeight - 12));

      setTooltipStyle({
        position: 'fixed',
        top,
        left,
        width: tooltipWidth,
        zIndex: 9999,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [active, currentStep, stepIndex]);

  if (!active || !currentStep) return null;

  const isIntroOrOutro = !currentStep?.target;

  // Centered modal (intro / outro)
  if (isCentered || isIntroOrOutro) {
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}
      >
        <div
          className="card p-6 shadow-2xl w-full max-w-md"
          style={{ maxWidth: '480px' }}
        >
          {/* Progress dots */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-1">
              {Array.from({ length: totalSteps })?.map((_, i) => (
                <div
                  key={i}
                  className="rounded-full transition-all"
                  style={{
                    width: i === stepIndex ? 20 : 5,
                    height: 5,
                    background: i === stepIndex ? 'var(--color-accent)' : 'var(--color-border)',
                  }}
                />
              ))}
            </div>
            <span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              {stepIndex + 1} / {totalSteps}
            </span>
          </div>

          {/* Icon */}
          <div className="flex items-center justify-center w-12 h-12 rounded-xl mb-4 mx-auto" style={{ background: 'var(--color-accent)' }}>
            <Icon name={isLastStep ? 'CheckCircle' : 'Map'} size={24} color="white" strokeWidth={2} />
          </div>

          {/* Content */}
          <h3 className="font-heading text-xl font-bold text-foreground text-center mb-3">{currentStep?.title}</h3>
          <p className="text-sm text-muted-foreground text-center mb-6" style={{ fontFamily: '"IBM Plex Sans", sans-serif', lineHeight: '1.6' }}>
            {currentStep?.description}
          </p>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <button
              onClick={isLastStep ? onComplete : onNext}
              className="w-full py-2.5 rounded-lg text-white font-semibold text-sm transition-opacity hover:opacity-90"
              style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}
            >
              {currentStep?.cta || (isLastStep ? 'Ir al Dashboard' : 'Siguiente')}
            </button>
            {!isLastStep && (
              <button
                onClick={onSkip}
                className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                {currentStep?.ctaSkip || 'Saltar tour'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Element highlight tooltip
  return (
    <>
      {/* Highlight overlay */}
      <div style={highlightStyle} />

      {/* Tooltip card */}
      <div
        style={tooltipStyle}
        className="card p-4 shadow-2xl"
      >
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(totalSteps, 12) })?.map((_, i) => (
              <div
                key={i}
                className="rounded-full transition-all"
                style={{
                  width: i === stepIndex ? 18 : 5,
                  height: 5,
                  background: i === stepIndex ? 'var(--color-accent)' : 'var(--color-border)',
                }}
              />
            ))}
          </div>
          <span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            {stepIndex + 1} / {totalSteps}
          </span>
        </div>

        {/* Content */}
        <h4 className="font-heading text-sm font-semibold text-foreground mb-1.5">{currentStep?.title}</h4>
        <p className="text-xs text-muted-foreground mb-4" style={{ fontFamily: '"IBM Plex Sans", sans-serif', lineHeight: '1.5' }}>
          {currentStep?.description}
        </p>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={onSkip}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
          >
            Saltar
          </button>
          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <button
                onClick={onPrev}
                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors text-foreground"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                <Icon name="ChevronLeft" size={12} color="currentColor" strokeWidth={2} />
                Anterior
              </button>
            )}
            <button
              onClick={isLastStep ? onComplete : onNext}
              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg text-white transition-opacity hover:opacity-90"
              style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}
            >
              {isLastStep ? (
                <><Icon name="CheckCircle" size={12} color="white" strokeWidth={2} /> Finalizar</>
              ) : (
                <>Siguiente <Icon name="ChevronRight" size={12} color="white" strokeWidth={2} /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
